import { type HTMLAttributes, memo, type ReactNode, useCallback, useMemo, useState } from 'react';

import { ContextMenu, renderContextMenuItems } from '@air/primitive-context-menu';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { type GetSelectionMenuOptionsType } from '~/components/Gallery/types';
import { TableRow } from '~/components/TableView/TableRow/TableRow';
import { type UseSelectableCardProps, useSelectableCard } from '~/hooks/useSelectableCard';
import { useSelectionAwareContextMenuOptions } from '~/hooks/useSelectionAwareContextMenuOptions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';
import { leftMouseEvent } from '~/utils/ClickUtils';

export type SelectableTableRowProps<Item extends SelectableGalleryItem> = {
  renderCells: (props: { isSelected: boolean; isHovering: boolean }) => ReactNode;
  contextMenuOptions: DropdownMenuOption[];
  getSelectionMenuOptions: GetSelectionMenuOptionsType;
  onRowClick: () => void;
  item: UseSelectableCardProps<Item>['item'];
  isSelectable: boolean;
  keyboardListenerProps?: HTMLAttributes<HTMLDivElement>;
};

const _SelectableTableRow = <Item extends SelectableGalleryItem>({
  item,
  renderCells,
  getSelectionMenuOptions,
  contextMenuOptions,
  onRowClick,
  isSelectable,
  keyboardListenerProps,
}: SelectableTableRowProps<Item>) => {
  const {
    isHovering,
    handleHover,
    handleUnhover,
    isSelected,
    handleItemDoubleClick,
    confirmItemSelection,
    preSelectItem,
    onFocus,
    onBlur,
    itemRef: rowRef,
    isHighlighted,
  } = useSelectableCard({
    item,
    onClick: onRowClick,
    isSelectable,
  });

  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const handleContextMenuOpenChange = useCallback((open: boolean) => {
    setIsContextMenuOpen(open);
  }, []);
  const onMouseDown = useMemo(
    () => (isContextMenuOpen ? undefined : leftMouseEvent(preSelectItem)),
    [isContextMenuOpen, preSelectItem],
  );

  const { contentClassName, options } = useSelectionAwareContextMenuOptions({
    id: item.id,
    contextMenuOptions,
    selectionMenuOptions: getSelectionMenuOptions('table-multi-select-context-menu'),
  });

  const className = useMemo(() => {
    const base =
      isSelected || isHighlighted ? 'outline rounded-6 outline-[2px] ads:outline-primary outline-macaw-300' : '';

    return isHovering ? `${base} ads:bg-hover-subtle bg-gray-3` : base;
  }, [isSelected, isHovering, isHighlighted]);

  return (
    <ContextMenu
      className={contentClassName}
      trigger={
        <TableRow
          tabIndex={0}
          onMouseDown={onMouseDown}
          onClick={confirmItemSelection}
          onDoubleClick={handleItemDoubleClick}
          onFocus={onFocus}
          onBlur={onBlur}
          onMouseEnter={handleHover}
          onMouseMove={handleHover}
          onMouseLeave={handleUnhover}
          ref={rowRef}
          className={className}
          {...keyboardListenerProps}
        >
          {renderCells({ isSelected, isHovering })}
        </TableRow>
      }
      onOpenChange={handleContextMenuOpenChange}
    >
      {renderContextMenuItems({ options })}
    </ContextMenu>
  );
};

// biome-ignore lint/plugin: memo erases generic params; typeof cast restores inference
export const SelectableTableRow = memo(_SelectableTableRow) as typeof _SelectableTableRow;
